<template>
  <BaseCard title="Account Information">
    <dl class="body-sm flex flex-col gap-y-1">
      <div class="flex items-start gap-x-2">
        <dt
          class="w-44 shrink-0 font-semibold md:w-60"
          v-text="'Name:'"
        />
        <dd v-text="userName" />
      </div>
      <div class="flex items-start gap-x-2">
        <dt
          class="w-44 shrink-0 font-semibold md:w-60"
          v-text="'Personal Email:'"
        />
        <dd v-text="profile?.email" />
      </div>
      <div class="flex items-start gap-x-2">
        <dt
          class="w-44 shrink-0 font-semibold md:w-60"
          v-text="'Secondary Email:'"
        />
        <dd v-text="profile?.secondary_email || '-'" />
      </div>
    </dl>
  </BaseCard>

  <BaseCard title="Personal Information">
    <dl class="body-sm flex flex-col gap-y-1">
      <div class="flex items-start gap-x-2">
        <dt
          class="w-44 shrink-0 font-semibold md:w-60"
          v-text="'Country:'"
        />
        <dd v-text="profile?.personal.country ?? '-'" />
      </div>
      <div class="flex items-start gap-x-2">
        <dt
          class="w-44 shrink-0 font-semibold md:w-60"
          v-text="'Address:'"
        />
        <dd v-text="fullAddress" />
      </div>
      <div class="flex items-start gap-x-2">
        <dt
          class="w-44 shrink-0 font-semibold md:w-60"
          v-text="'Phone Number:'"
        />
        <dd v-text="profile?.customer_contact_number ?? '-'" />
      </div>
      <div class="flex items-start gap-x-2">
        <dt
          class="w-44 shrink-0 font-semibold md:w-60"
          v-text="'Secondary Phone Number:'"
        />
        <dd v-text="profile?.personal.secondary_phone ?? '-'" />
      </div>
      <div class="flex items-start gap-x-2">
        <dt
          class="w-44 shrink-0 font-semibold md:w-60"
          v-text="'Phone Type:'"
        />
        <dd v-text="phoneType" />
      </div>
      <div class="flex items-start gap-x-2">
        <dt
          class="w-44 shrink-0 font-semibold md:w-60"
          v-text="'Extension:'"
        />
        <dd v-text="profile?.personal.phone_ext ?? '-'" />
      </div>
    </dl>
  </BaseCard>

  <BaseCard title="Professional information">
    <dl class="body-sm flex flex-col gap-y-1">
      <div class="flex items-start gap-x-2">
        <dt
          class="w-44 shrink-0 font-semibold md:w-60"
          v-text="'Primary Place of Employment:'"
        />
        <dd v-text="profile?.professional.primary_employment || '-'" />
      </div>
      <div class="flex items-start gap-x-2">
        <dt
          class="w-44 shrink-0 font-semibold md:w-60"
          v-text="'Current Employer Name:'"
        />
        <dd v-text="profile?.professional.employer_name || '-'" />
      </div>
      <div class="flex items-start gap-x-2">
        <dt
          class="w-44 shrink-0 font-semibold md:w-60"
          v-text="'Current Employer City & State:'"
        />
        <dd v-text="employerCityAndState" />
      </div>
      <div class="flex items-start gap-x-2">
        <dt
          class="w-44 shrink-0 font-semibold md:w-60"
          v-text="'Management Level:'"
        />
        <dd v-text="profile?.professional.management_level || '-'" />
      </div>
      <div class="flex items-start gap-x-2">
        <dt
          class="w-44 shrink-0 font-semibold md:w-60"
          v-text="'Past Wound Care Certifications:'"
        />
        <dd v-text="pastCertificates" />
      </div>
    </dl>
  </BaseCard>

  <LicensesTable :licenses="licenses" />
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import BaseCard from '@js/components/global/BaseCard.vue'
import LicensesTable from '@js/components/account/profile/LicensesTable.vue'

import { phoneTypes } from '@js/utils/form'

import { CustomerLicense } from '@js/api/types/customer/CustomerLicense'
import { CustomerProfile } from '@js/api/types/customer/CustomerProfile'

defineOptions({
  name: 'ProfileDetails',
})

const props = defineProps<{
  licenses: CustomerLicense[]
  profile: CustomerProfile | null
}>()

const userName = computed(() =>
  props.profile?.middle_name
    ? `${props.profile?.first_name} ${props.profile?.middle_name} ${props.profile?.last_name}`
    : `${props.profile?.first_name} ${props.profile?.last_name}`,
)

const fullAddress = computed(() => {
  if (!props.profile) {
    return '-'
  }

  const { address, address_two, city, country, postcode, state } = props.profile.personal

  if (!address || !city || !country || !postcode ) {
    return '-'
  }

  const addressLine = address_two ? `${address}, ${address_two}` : address

  return `${addressLine}, ${city}, ${postcode}, ${country}`
})

const phoneType = computed(() => {
  if (!props.profile) {
    return '-'
  }

  return props.profile.personal.phone_type
    ? phoneTypes.find(type => type.value === props.profile?.personal.phone_type)?.label
    : '-'
})

const employerCityAndState = computed(() => {
  if (!props.profile) {
    return '-'
  }

  const { professional } = props.profile

  if (!professional.employer_city || !professional.employer_state) {
    return '-'
  }

  return `${professional.employer_city}, ${professional.employer_state}`
})

const pastCertificates = computed(() => {
  if (!props.profile) {
    return '-'
  }

  const { professional } = props.profile

  return professional.wc_certificates.length > 0
    ? professional.wc_certificates.join(', ')
    : '-'
})
</script>
